import type { JSONContent } from '@tiptap/core';
import type { NumberingFormatDefinition } from '@tiptap-pro/extension-export-docx';

import { env } from 'config';
import Logger from 'utils/logger';

// Tiptap Conversion REST — DOCX import.
// https://tiptap.dev/docs/conversion/import/docx/rest-api
const DEFAULT_TIPTAP_IMPORT_DOCX_URL = 'https://api.tiptap.dev/v2/convert/import/docx';

export const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Public core-api endpoint Tiptap's Conversion service POSTs embedded images to.
export function convertImageUploadUrlFromApiBase(apiBase: string): string {
  return new URL('webhook/tiptap/convert-image/', new URL(apiBase).origin + '/').href;
}

export function getConvertImageUploadUrl(): string {
  return convertImageUploadUrlFromApiBase(env.REACT_APP_API_URL || '');
}

export class DocxImportError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'DocxImportError';
    this.status = status;
  }
}

export interface ImportDocxImageUpload {
  url: string;
  token: string;
  entityId: string;
}

export interface ImportDocxOptions {
  file: File;
  token: string;
  endpoint?: string;
  signal?: AbortSignal;
  imageUpload?: ImportDocxImageUpload;
}

export interface ImportedHeaderFooter {
  header: JSONContent | null;
  footer: JSONContent | null;
  headerFirstPage: JSONContent | null;
  footerFirstPage: JSONContent | null;
  differentFirstPage: boolean;
}

export interface ImportDocxResult {
  content: JSONContent;
  numberingFormats: NumberingFormatDefinition[];
  headerFooter: ImportedHeaderFooter | null;
}

// Tiptap's DOCX-import REST response. Footnotes are intentionally ignored (out of scope).
interface DocxImportResponse {
  data?: {
    content?: JSONContent;
    numberingFormats?: NumberingFormatDefinition[];
    header?: JSONContent | null;
    footer?: JSONContent | null;
    headerFirstPage?: JSONContent | null;
    footerFirstPage?: JSONContent | null;
    headerOdd?: JSONContent | null;
    headerEven?: JSONContent | null;
    footerOdd?: JSONContent | null;
    footerEven?: JSONContent | null;
  };
}

const PLACEHOLDERS = { page: 'page', total: 'total' };

export function hasSlotContent(doc: JSONContent | null | undefined): doc is JSONContent {
  if (!doc || typeof doc !== 'object') return false;
  const visit = (node: JSONContent): boolean => {
    if (node.type === 'text') return Boolean(node.text?.trim());
    if (node.type === 'image') return true;
    return Array.isArray(node.content) && node.content.some(visit);
  };
  return visit(doc);
}

function extractHeaderFooter(data: NonNullable<DocxImportResponse['data']>): ImportedHeaderFooter | null {
  const odd = [data.headerOdd, data.headerEven, data.footerOdd, data.footerEven].some(hasSlotContent);
  if (odd) {
    Logger.warn('Dropped odd/even headers and footers from docx import (not supported)');
  }
  const pick = (doc: JSONContent | null | undefined): JSONContent | null => (hasSlotContent(doc) ? doc : null);
  const headerFirstPage = pick(data.headerFirstPage);
  const footerFirstPage = pick(data.footerFirstPage);
  const headerFooter: ImportedHeaderFooter = {
    header: pick(data.header),
    footer: pick(data.footer),
    headerFirstPage,
    footerFirstPage,
    differentFirstPage: Boolean(data.headerFirstPage || data.footerFirstPage),
  };
  const hasAnything = [headerFooter.header, headerFooter.footer, headerFirstPage, footerFirstPage].some(Boolean);
  return hasAnything ? headerFooter : null;
}

function safeExtractHeaderFooter(data: NonNullable<DocxImportResponse['data']>): ImportedHeaderFooter | null {
  try {
    return extractHeaderFooter(data);
  } catch (error) {
    Logger.warn('Dropped malformed headers and footers from docx import', error);
    return null;
  }
}

export async function importDocx({
  file,
  token,
  endpoint,
  signal,
  imageUpload,
}: ImportDocxOptions): Promise<ImportDocxResult> {
  const url = endpoint || DEFAULT_TIPTAP_IMPORT_DOCX_URL;

  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('placeholders', JSON.stringify(PLACEHOLDERS));
  formData.append('taskLists', 'import');

  if (imageUpload) {
    formData.append(
      'imageUploadConfig',
      JSON.stringify({
        url: imageUpload.url,
        headers: { Authorization: `Bearer ${imageUpload.token}` },
        queryParams: { entityId: imageUpload.entityId },
      })
    );
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      body: formData,
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (e) {
    if ((e as { name?: string })?.name === 'AbortError') {
      throw e;
    }
    throw new DocxImportError('Could not reach the conversion service. Check your connection and try again.');
  }

  if (!response.ok) {
    throw new DocxImportError(`Conversion failed (HTTP ${response.status}).`, response.status);
  }

  let payload: DocxImportResponse;
  try {
    payload = (await response.json()) as DocxImportResponse;
  } catch {
    throw new DocxImportError('Conversion returned an unexpected response.');
  }

  const content = payload?.data?.content;
  if (!content || typeof content !== 'object') {
    throw new DocxImportError('Conversion returned no document content.');
  }

  const rawNumberingFormats = payload?.data?.numberingFormats ?? [];
  const numberingFormats = rawNumberingFormats.filter(
    (format) => Array.isArray(format?.levels) && format.levels.length > 0
  );
  if (numberingFormats.length < rawNumberingFormats.length) {
    Logger.warn('Dropped malformed numbering format(s) from docx import (empty levels array)', {
      droppedIds: rawNumberingFormats.filter((format) => !numberingFormats.includes(format)).map((f) => f.id),
    });
  }

  return { content, numberingFormats, headerFooter: safeExtractHeaderFooter(payload.data ?? {}) };
}
